import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/nextjs';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useState } from 'react';

import { Alert, Box, Button, Flex, Icon, Loader, Text } from '~/components/blocks';
import { useFetchInsuranceCards } from '~/components/partials/Telemedicine/PatientProfilePanel/use-fetch-insurance_cards';
import { TabMap } from '~/constants/tab';
import { filePageInfoState, tabState } from '~/state/partials/telemedicine_patient_profile/atoms';
import { telemedicinePatientFileAreaState } from '~/state/reception_drawer/atoms';
import { isPdf } from '~/utils/file';
import { openWithNoOpener } from '~/utils/window';

import { ImgViewer } from './ImgViewer';
import { PdfViewer } from './PdfViewer';
import { useFetchAppointment } from './use-fetch-appointment';

const DEFAULT_HEIGHT = '130px';

const PreviewButton = styled(Button)(({ theme }) =>
  css({
    position: 'absolute',
    top: '50%',
    left: '50%',
    display: 'flex',
    width: '40px',
    height: '40px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radii.circle,
    opacity: 0,
    transform: 'translate(-50%,-50%)',
    transition: `opacity linier ${theme.transitions.fast}`,
  }),
);

const Content = styled(Box)(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.bg,
    cursor: 'pointer',
    minHeight: DEFAULT_HEIGHT,
    position: 'relative',
    [`&:hover > ${PreviewButton}`]: {
      opacity: 1,
    },
  }),
);

export const InsuranceCard = () => {
  const theme = useTheme();
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [hasPdfError, setHasPdfError] = useState(false);
  const { appointmentId, patientId } = useAtomValue(telemedicinePatientFileAreaState);
  const setTabState = useSetAtom(tabState);
  const setFilePageInfoState = useSetAtom(filePageInfoState);
  const {
    qualificationConfirmationMethod,
    isActivePatient,
    loading: appointmentLoading,
  } = useFetchAppointment({ appointmentId });

  const willFetchInsuranceCards = qualificationConfirmationMethod === 'insurance_card';
  const {
    loading: insuranceCardsLoading,
    timeout,
    hasCertificate,
    error,
    insuranceCards,
  } = useFetchInsuranceCards(willFetchInsuranceCards, patientId);
  const hasPdf = insuranceCards.some((card) => isPdf(card.attachment.contentType));

  const handleClickCertificate = useCallback(() => {
    if (patientId) {
      setTabState({ type: TabMap.file });
      setFilePageInfoState((_state) => ({
        ..._state,
        patientId,
        page: 1,
        filter: { isSent: false, isReceived: true },
      }));
    }
  }, [patientId, setTabState, setFilePageInfoState]);

  const handleClick = useCallback(() => {
    if (patientId) {
      openWithNoOpener(`/insurance_card_preview/${patientId}`, {
        target: patientId,
        width: 640,
        height: 720,
        bgColor: '#525659',
      });
    }
  }, [patientId]);

  const handlePdfError = useCallback((error: Error) => {
    setHasPdfError(true);
    Sentry.captureMessage(`react-pdf Error: message=${error.message}`);
  }, []);
  const handlePdfLoad = useCallback(() => setIsPdfLoading(false), []);

  return (
    <Flex flexDirection="column">
      <Flex marginBottom={theme.space.l}>
        <Icon size="xl" icon="insurance" />
        <Text fontWeight="bold" marginLeft={theme.space.xs}>
          保険情報
        </Text>
      </Flex>
      <Box>
        {qualificationConfirmationMethod === 'individual_number_card' ? (
          <Alert status="info" marginTop={theme.space.m}>
            オンライン資格確認を希望しています。服薬指導日までに、オンライン資格確認等システムなどにて資格情報をご確認ください。確認できない場合は、患者様へ同意登録を促してください
          </Alert>
        ) : timeout ? (
          <Alert marginTop={theme.space.m}>
            資格確認書の取得に時間がかかっています。
            <br />
            しばらく待ってから、再度アクセスしてください
          </Alert>
        ) : error ? (
          <Box padding={theme.space.m}>
            <Alert status="error">取得できませんでした</Alert>
          </Box>
        ) : !isActivePatient ? (
          <Box padding={theme.space.m}>
            <Alert status="info">退会済みのため表示できません</Alert>
          </Box>
        ) : appointmentLoading || insuranceCardsLoading ? (
          <Box
            backgroundColor={theme.colors.background.bg}
            height={DEFAULT_HEIGHT}
            position="relative"
          >
            <Loader open inside logoSize="half" />
          </Box>
        ) : insuranceCards.length > 0 ? (
          <Content onClick={hasPdf && (hasPdfError || isPdfLoading) ? undefined : handleClick}>
            {insuranceCards.map((card) => {
              if (isPdf(card.attachment.contentType)) {
                return (
                  <PdfViewer
                    key={card.attachment.id}
                    src={card.attachment.url}
                    onError={handlePdfError}
                    onLoad={handlePdfLoad}
                  />
                );
              } else {
                return (
                  <ImgViewer
                    key={card.attachment.id}
                    alt={card.attachment.title}
                    src={card.attachment.url}
                  />
                );
              }
            })}
          </Content>
        ) : (
          <Box padding={theme.space.m}>
            <Alert status="info">登録されていません</Alert>
          </Box>
        )}
        {hasCertificate && (
          <Box marginTop={theme.space.xl} backgroundColor={theme.colors.background.default}>
            <Button size="s" onClick={handleClickCertificate}>
              <Icon icon="document" size="m" color="black" />
              公費受給者証を確認
            </Button>
          </Box>
        )}
      </Box>
    </Flex>
  );
};
